import { create } from 'zustand';
import { normalizeStoreAssetUrl } from '../lib/mediaUrl';

// في التطوير: البروكسي يمرر /api إلى الباكند
const BACKEND_URL = import.meta.env.DEV
  ? ''
  : (import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com').replace(/\/$/, '');

interface Branch {
  id: number;
  name: string;
  isDefault: boolean;
  comingSoon?: boolean;
}

interface StoreData {
  name: string;
  logo?: string;   // رابط شعار المتجر
  banner?: string; // رابط بنر المتجر
  /** يتغيّر عند تحديث بيانات المتجر (لإعادة رسم الشعار وكسر كاش الصورة) */
  updatedAt?: string;
  status: string;
  /** المتجر مشغول (ضغط على الطلبات) — يوقف الدفع مؤقتاً ويظهر تنبيهاً للعميل */
  busy?: boolean;
  shipping: number;
  /** رقم واتساب المتجر (بدون + أو مسافات، يُستخدم في زر التواصل) */
  whatsapp?: string | null;
  workingHours: {
    open: string;
    close: string;
  };
  /** تفعيل عرض الشحن المجاني (حسب المبلغ) */
  freeShippingEnabled?: boolean;
  /** الحد الأدنى بالريال لتفعيل الشحن المجاني */
  freeShippingThreshold?: number;
  /** هل عرض الشحن المجاني مفعّل حالياً (مع احترام الفترة الزمنية) */
  freeShippingActive?: boolean;
  freeShippingValidFrom?: string | null;
  freeShippingValidUntil?: string | null;
  /** روابط تطبيقات التوصيل */
  deliveryChefzUrl?: string;
  deliveryJahezUrl?: string;
  deliveryHungerStationUrl?: string;
  deliveryKeetaUrl?: string;
  branches: Branch[];
}

interface StoreInfoState {
  data: StoreData | null;
  isLoading: boolean;
  error: string | null;
  selectedBranchId: number | null;
  hasFetched: boolean;
  /** جلب بيانات المتجر؛ مع force=true يتجاهل hasFetched (مثلاً بعد تحديث الشعار من التطبيق) */
  fetchStoreData: (force?: boolean) => Promise<void>;
  setSelectedBranch: (id: number) => void;
}

const MAX_RETRIES = 3;
// Exponential backoff: 1s → 3s → 7s (يغطي cold start لـ Railway)
const RETRY_DELAYS_MS = [1000, 3000, 7000];

/** تنظيف رسالة الخطأ — لا نُظهر روابط أو مسارات API للمستخدم */
function sanitizeStoreError(raw: string | undefined): string {
  if (!raw || typeof raw !== 'string') return 'فشل الاتصال بالخادم';
  if (/https?:\/\//i.test(raw) || /\/api\//i.test(raw)) return 'فشل الاتصال بالخادم';
  if (/failed to fetch|network error|load failed/i.test(raw)) return 'فشل الاتصال بالخادم';
  return raw.length > 120 ? 'فشل الاتصال بالخادم' : raw;
}

/** كشف البوتات — لتجنب إرسال ضجيج Sentry من الزواحف */
function isBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /bot|crawl|spider|adsbotcrawler|snapchatads|facebookexternalhit|AdsBot/i.test(
    navigator.userAgent
  );
}

// متغير خارجي لمنع الطلبات المتزامنة
let isFetchingStoreData = false;

export const useStoreInfo = create<StoreInfoState>((set, get) => ({
  data: null,
  isLoading: false,
  error: null,
  selectedBranchId: null,
  hasFetched: false,
  fetchStoreData: async (force = false) => {
    const state = get();

    // منع الطلبات المتزامنة؛ أما hasFetched فيُتخطّى عند force لتحديث الشعار وغيره بعد التطبيق
    if (state.isLoading || isFetchingStoreData) {
      return;
    }
    if (!force && state.hasFetched) {
      return;
    }

    isFetchingStoreData = true;
    set({ isLoading: true, error: null });

    let lastError: any = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`${BACKEND_URL}/api/store?t=${timestamp}`, {
          cache: 'no-cache',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!response.ok) {
          throw new Error(`فشل جلب بيانات المتجر (${response.status})`);
        }
        const json = await response.json();
        const store = json?.store || json;
        const branches = store?.branches || [];
        const defaultBranch = branches.find((b: Branch) => b.isDefault) || branches[0];
        set({
          data: store,
          selectedBranchId: defaultBranch ? defaultBranch.id : null,
          isLoading: false,
          hasFetched: true,
          error: null,
        });
        if (typeof document !== 'undefined' && store?.logo) {
          let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = normalizeStoreAssetUrl(store.logo);
        }
        isFetchingStoreData = false;
        return;
      } catch (err: any) {
        lastError = err;
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
        }
      }
    }

    // فشلت جميع المحاولات — لا نُبلّغ Sentry إذا كان المصدر بوت
    const displayMessage = sanitizeStoreError(lastError?.message);
    if (!isBot()) {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(lastError, {
          tags: { feature: 'store_fetch' },
          extra: { message: lastError?.message, url: `${BACKEND_URL}/api/store`, attempts: MAX_RETRIES },
        });
      } catch (_) {}
    }

    set({
      data: null,
      error: displayMessage,
      isLoading: false,
      hasFetched: false, // يبقى false ليُعيد المحاولة عند العودة للصفحة
    });
    isFetchingStoreData = false;
  },
  setSelectedBranch: (id) => set({ selectedBranchId: id }),
}));
